import { typeof as _typeof, slicedToArray as _slicedToArray, createForOfIteratorHelper as _createForOfIteratorHelper, toArray as _toArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useBlockEnvironment } from '@zillow/cms-blocks/BlockEnvironment';
import { Box, ProgressStepper } from '@zillow/constellation';
import React, { useCallback } from 'react';
import { getTrackingParamsFromCurrentUrl } from '../../utils/utm.js';
import OverlappingStack from './OverlappingStack.js';
import { getNameError, getEmailError, getPhoneError, getOrgError } from './utils.js';

var INVISIBLE_SX = {
  visibility: 'hidden',
  pointerEvents: 'none',
  userSelect: 'none'
};
function FormStepper(props) {
  var steps = React.Children.toArray(props.children).filter(function (child) {
    return _typeof(child) === 'object' && child !== null && 'type' in child && child.type === FormStepper.Step;
  });

  // Take the first footer element, the rest are ignored
  var _React$Children$toArr = React.Children.toArray(props.children).filter(function (child) {
      return _typeof(child) === 'object' && child !== null && 'type' in child && child.type === FormStepper.Footer;
    }),
    _React$Children$toArr2 = _slicedToArray(_React$Children$toArr, 1),
    footer = _React$Children$toArr2[0];

  // Gets the first visible step. If another has visbile=true it is treated as hidden.
  var visibleStep = steps.find(function (step) {
    return step.props.name === props.visibleStepName;
  });
  var hasProgressBar = !!(visibleStep !== null && visibleStep !== void 0 && visibleStep.props.progressBar);
  var _useBlockEnvironment = useBlockEnvironment(),
    _useBlockEnvironment$ = _useBlockEnvironment.environment,
    environment = _useBlockEnvironment$ === void 0 ? 'prod' : _useBlockEnvironment$;
  return /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Box, {
    as: ProgressStepper,
    sx: Object.assign({}, !hasProgressBar && INVISIBLE_SX, {
      margin: '0 auto $spacing.lg',
      maxWidth: '540px'
    }),
    activeStepIndex: hasProgressBar ? visibleStep.props.progressBar.current - 1 : -1,
    steps: hasProgressBar ? Array.from({
      length: visibleStep.props.progressBar.total
    }, function (_, i) {
      return {
        label: '',
        appearance: i < visibleStep.props.progressBar.current ? 'complete' : undefined
      };
    }) : ['']
  }), /*#__PURE__*/React.createElement(OverlappingStack, {
    sx: {
      marginBottom: '$spacing.xxl',
      maxWidth: '540px',
      margin: '0 auto'
    },
    as: "form"
    // If not stable, this ref fires every render.
    ,
    ref: useCallback(function prefileUtmParams(form) {
      if (!form) {
        return;
      }
      var currentTrackingValues = getTrackingParamsFromCurrentUrl();
      for (var _i = 0, _Object$entries = Object.entries(currentTrackingValues); _i < _Object$entries.length; _i++) {
        var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
          key = _Object$entries$_i[0],
          value = _Object$entries$_i[1];
        var element = form.elements[key];
        if (element) {
          element.value = value;
        }
      }
    }, []),
    onSubmit: function onSubmit(e) {
      return new Promise(function ($return, $error) {
        var _props$onSubmitStart, _props$onPrepareSubmi, formData, rawData, _iterator, _step, _step$value, key, value, data, validationErrors, hasValidationErrors, _props$onSubmitError, url, _url, _data$Name$split, _data$Name$split2, firstName, lastNameParts, res;
        e.preventDefault();
        if (!props.onShouldSubmit()) {
          return $return();
        }
        (_props$onSubmitStart = props.onSubmitStart) === null || _props$onSubmitStart === void 0 || _props$onSubmitStart.call(props);
        formData = new FormData(e.currentTarget);
        rawData = {};
        _iterator = _createForOfIteratorHelper(formData.entries());
        try {
          for (_iterator.s(); !(_step = _iterator.n()).done;) {
            _step$value = _slicedToArray(_step.value, 2), key = _step$value[0], value = _step$value[1];
            rawData[key] = typeof value === 'string' ? value : '';
          }
        } catch (err) {
          _iterator.e(err);
        } finally {
          _iterator.f();
        }
        data = ((_props$onPrepareSubmi = props.onPrepareSubmitData) === null || _props$onPrepareSubmi === void 0 ? void 0 : _props$onPrepareSubmi.call(props, rawData)) || rawData;
        validationErrors = {
          Name: getNameError(data.Name),
          Email: getEmailError(data.Email),
          Phone: getPhoneError(data.Phone),
          Brokerage_Size: getOrgError(data.Brokerage_Size)
        };
        hasValidationErrors = Object.values(validationErrors).some(function (error) {
          return Boolean(error);
        });
        if (hasValidationErrors) {
          (_props$onSubmitError = props.onSubmitError) === null || _props$onSubmitError === void 0 || _props$onSubmitError.call(props);
          return $return();
        }

        // Remove query parameters from URLs before submission
        if (data.URL) {
          try {
            url = new URL(data.URL);
            data.URL = "" + url.origin + url.pathname;
          } catch (_unused) {
            // If URL is invalid, remove it to prevent Salesforce errors
            delete data.URL;
          }
        }
        if (data.URL_Referrer) {
          try {
            _url = new URL(data.URL_Referrer);
            data.URL_Referrer = "" + _url.origin + _url.pathname;
          } catch (_unused2) {
            // If URL is invalid, remove it to prevent Salesforce errors
            delete data.URL_Referrer;
          }
        }

        // Apex - split Name field into first and last name
        if (typeof data.Name === 'string') {
          _data$Name$split = data.Name.split(' '), _data$Name$split2 = _toArray(_data$Name$split), firstName = _data$Name$split2[0], lastNameParts = _data$Name$split2.slice(1);
          data['First_Name'] = firstName;
          data['Last_Name'] = lastNameParts.join(' ');
        }

        // Apex tfa_dbFormId
        data['tfa_dbFormId'] = 'WORDPRESS';
        data['Ok_to_contact'] = '1';
        var $Try_4_Post = function () {
          try {
            return $return();
          } catch ($boundEx) {
            return $error($boundEx);
          }
        };
        var $Try_4_Catch = function (error) {
          try {
            props.onSubmitError();
            return $Try_4_Post();
          } catch ($boundEx) {
            return $error($boundEx);
          }
        };
        try {
          return Promise.resolve(fetch(environment === 'prod' ? 'https://zg-api.com/interested-party/submit' : 'https://zg-api.test.zillow.net/interested-party/submit', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: new URLSearchParams(data).toString()
          })).then(function ($await_5) {
            try {
              res = $await_5;
              if (res.status !== 200) {
                throw new Error("Got failed status code: \"" + res.status + "\"");
              }
              props.onSubmitSuccess();
              return $Try_4_Post();
            } catch ($boundEx) {
              return $Try_4_Catch($boundEx);
            }
          }, $Try_4_Catch);
        } catch (error) {
          $Try_4_Catch();
        }
      });
    },
    noValidate: true
  }, /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: "utm_medium"
  }), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: "utm_term"
  }), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: "utm_content"
  }), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: "utm_source"
  }), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: "utm_campaign"
  }), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: "Internal_Tracking_Code"
  }), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: "URL"
  }), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: "URL_Referrer"
  }), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: "Test_Variation"
  }), steps.map(function (step) {
    return /*#__PURE__*/React.createElement(Box, {
      key: step.props.name,
      sx: step.props.name === props.visibleStepName ? undefined : INVISIBLE_SX
    }, step);
  })), footer && /*#__PURE__*/React.createElement(Box, {
    sx: {
      maxWidth: '$breakpoints.lg',
      margin: '0 auto'
    }
  }, footer));
}
FormStepper.Step = function (props) {
  return props.children;
};
FormStepper.Footer = function (props) {
  return props.children;
};

export { INVISIBLE_SX, FormStepper as default };
